// ═══════════════════════════════════════════════════════════════════════════════════════════════════
// agentReasoning.ts — typed API client for the reasoning-effort policy surface
// ═══════════════════════════════════════════════════════════════════════════════════════════════════
//
// Backs the Reasoning effort card on the agent detail page. Five endpoints,
// all surfacing the centralized IReasoningEffortManager logic to the UI:
//
//   GET  /api/AgentReasoning/Levels                       — populate dropdown
//   GET  /api/AgentReasoning/Agent/{agentId}              — current state + provenance
//   POST /api/AgentReasoning/Agent/{agentId}/Set          — change level (audited)
//   GET  /api/AgentReasoning/Agent/{agentId}/History      — change history
//   GET  /api/AgentReasoning/Snapshot?merchantId=…        — full-merchant table (future)
//
// Every endpoint returns OperationResult<T> per project convention; this
// module unwraps and throws on failure so callers get a clean Promise<T>.
// ═══════════════════════════════════════════════════════════════════════════════════════════════════

import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ────────────────────────────────────────────────────────────────────────────
// Types — match the C# DTOs in AgentReasoningController.cs
// ────────────────────────────────────────────────────────────────────────────

export interface ReasoningLevel {
  id: number;
  name: string;            // "Low" | "Medium" | "High"
  wireValue: string;       // "low"  | "medium" | "high"
  description: string;     // Authoring guidance shown in dropdown
  displayOrder: number;
}

export interface AgentReasoningState {
  /** Stored preference on ai.AiAgent.ReasoningEffortLevelId. Always non-zero post-Phase 1 backfill. */
  preferredLevelId: number;
  preferredLevelName: string;
  preferredLevelWireValue: string;
  preferredLevelDescription: string;

  /** What ResolveAsync returns RIGHT NOW for this agent. May diverge from preferred (e.g. model doesn't support reasoning → resolved is None). */
  resolvedLevelId: number | null;
  resolvedLevelName: string;     // "Medium" | "(none)"
  resolvedWireValue: string;     // "low"/"medium"/"high"/""
  resolvedSource: EffortSource;  // The rung that fired
  resolvedRationale: string;     // Human-readable "why"

  /** False → the param is suppressed at runtime. UI hides effort-related controls or shows a hint. */
  shouldInject: boolean;

  /** Drives the capability warning banner. */
  modelSupportsReasoning: boolean;
  modelName: string;
}

/**
 * Mirrors OO.Business.AIInfo.Reasoning.EffortSource. The string values are
 * enum names (PascalCase) — the controller serializes via .ToString().
 */
export type EffortSource =
  | 'ModelDoesNotSupport'  // Rung 0 capability gate fired — non-reasoning model
  | 'InheritedFromCaller'  // Rung 1 — A2A peer consult inherited the caller's effort
  | 'AgentOverride'        // Rung 2 — agent's explicit ReasoningEffortLevelId
  | 'ModelDefault'         // Rung 3 — legacy ai.AIModel.ReasoningEffortLevel string
  | 'ApiAutoDetectMedium'  // Rung 4 — API.DefaultModelIsReasoning=true → medium
  | 'NoneConfigured';      // Rung 5 — nothing matched, no injection

export interface ReasoningHistoryRow {
  id: number;
  oldLevelId: number | null;       // null on first-time set
  oldLevelName: string | null;
  newLevelId: number;
  newLevelName: string;
  reason: string | null;
  changedByUserId: number;
  changedAtUtc: string;            // ISO timestamp
}

export interface SetReasoningLevelRequest {
  newLevelId: number;
  reason?: string;
}

export interface ReasoningSnapshotRow {
  agentId: number;
  agentDisplayName: string;
  resolvedLevelId: number;
  resolvedLevelName: string;
  resolvedWireValue: string;
  source: EffortSource;
  rationale: string;
}

// ────────────────────────────────────────────────────────────────────────────
// API calls
// ────────────────────────────────────────────────────────────────────────────

/** All active levels in display order. Cache-friendly — changes near zero times per day. */
export async function getReasoningLevels(): Promise<ReasoningLevel[]> {
  const res = await apiGet<OperationResult<ReasoningLevel[]>>('/api/AgentReasoning/Levels');
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load reasoning levels');
  return res.data ?? [];
}

/** Current preferred + resolved state for one agent. */
export async function getAgentReasoning(agentId: number): Promise<AgentReasoningState> {
  const res = await apiGet<OperationResult<AgentReasoningState>>(`/api/AgentReasoning/Agent/${agentId}`);
  if (!res.isSuccess || !res.data) throw new Error(res.message || `Failed to load reasoning for agent ${agentId}`);
  return res.data;
}

/**
 * Update the agent's preferred level. Writes one audit row to
 * ai.AgentReasoningEffortHistory. Returns the freshly-resolved state so the
 * UI can update without a second round-trip.
 */
export async function setAgentReasoning(
  agentId: number,
  req: SetReasoningLevelRequest,
): Promise<AgentReasoningState> {
  const res = await apiPost<OperationResult<AgentReasoningState>>(
    `/api/AgentReasoning/Agent/${agentId}/Set`,
    req,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to update reasoning level');
  return res.data;
}

/** History of level changes for one agent, newest first. Drives the audit popover. */
export async function getReasoningHistory(
  agentId: number,
  take: number = 50,
): Promise<ReasoningHistoryRow[]> {
  const res = await apiGet<OperationResult<ReasoningHistoryRow[]>>(
    `/api/AgentReasoning/Agent/${agentId}/History`,
    { take },
  );
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load reasoning history');
  return res.data ?? [];
}

/** Full-merchant snapshot — backs the future Reasoning dashboard tab. Expensive; call on demand. */
export async function getReasoningSnapshot(merchantId: number): Promise<ReasoningSnapshotRow[]> {
  const res = await apiGet<OperationResult<ReasoningSnapshotRow[]>>(
    '/api/AgentReasoning/Snapshot',
    { merchantId },
  );
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load reasoning snapshot');
  return res.data ?? [];
}
